'use client';

import { useEffect } from 'react';
import i18n from 'i18next';
import {
  applyA11ySettingsToDocument,
  readA11ySettingsFromStorage,
} from '@/lib/lyrra-a11y-settings';
import { LYRRA_A11Y_UPDATED_EVENT } from '@/hooks/useKeyboardNavigationSetting';

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur']);

function applyDocumentLanguage(code: string) {
  if (typeof document === 'undefined') return;
  const normalized = (code || 'fr').trim();
  document.documentElement.lang = normalized;
  const base = normalized.split('-')[0]?.toLowerCase() ?? 'fr';
  document.documentElement.dir = RTL_LANGS.has(base) ? 'rtl' : 'ltr';
}

/**
 * WCAG 3.1.1 (Langue de la page) + sens de lecture pour l’arabe / hébreu.
 * Synchronise <html lang> et dir avec i18next à chaque changement de langue.
 */
export function DocumentA11ySync() {
  useEffect(() => {
    applyDocumentLanguage(i18n.language);
    const onLang = (lng: string) => applyDocumentLanguage(lng);
    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, []);

  /** Réapplique les réglages accessibilité (malvoyance, dys, etc.) au chargement et après sync. */
  useEffect(() => {
    applyA11ySettingsToDocument(readA11ySettingsFromStorage());
    const onA11y = () => applyA11ySettingsToDocument(readA11ySettingsFromStorage());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11y);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11y);
  }, []);

  return null;
}
